import React, { useState, useRef, useEffect, useMemo } from 'react';
import { PermissionNotice, isForbidden } from '../../components/PermissionNotice';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiClient, openUploadDownload, getApiErrorMessage } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';
import { useIndustry } from '../../contexts/IndustryContext';
import DatePicker from '../../components/DatePicker';
import ProvinceSelect from '../../components/ProvinceSelect';
import { getRoleLabel, ALL_TENANT_ROLES, canSetJobPayExemption } from '../../lib/roles';
import { useAuth } from '../../contexts/AuthContext';
import { queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useTeamPermissions } from '../../hooks/useTeamPermissions';
import {
  JOB_PAY_EXEMPTIONS,
  JOB_PAY_EXEMPTION_LABELS,
  JOB_PAY_EXEMPTION_DESCRIPTIONS,
  JOB_PAID_LABEL,
  isJobPayExempt,
  isJobPayExemption,
  jobPayArrangementLabel,
  type JobPayExemption,
} from '../../constants/job-pay-exemption';
import { CERT_TYPES, CERT_TYPE_LABEL_MAP as CERT_TYPE_LABELS } from '../../lib/certTypes';
import { fmtCurrency, fmtPayPercentage } from '../../lib/display-labels';
import {
  LINE_ITEM_TYPES,
  LINE_ITEM_TYPE_LABELS,
  getLineItemTypeLabel,
  toLineItemType,
  type LineItemType,
} from '../../constants/line-item-types';
import {
  ArrowLeft, Mail, Phone, Calendar, DollarSign, Shield, ChevronRight,
  CheckCircle, AlertCircle, AlertTriangle, Loader2, Send, Trash2, Edit3, Save, X,
  Plus, User, Wrench, XCircle, Upload, Eye, EyeOff, Clock, RotateCcw, Pause, History,
} from 'lucide-react';
import { useHiddenLineItemTypes } from '../../hooks/useLineItemPrefs';
import { payRateGridTypes } from '../../lib/line-item-prefs';
import { PAY_BASIS_LABELS, dialogBasisOptions, payBasisLabel, PERCENTAGE_BASE_RATE } from '../../lib/pay-basis';
import { useDefaultPayBasis } from '../../hooks/useDefaultPayBasis';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { usePositions } from '../../hooks/usePositions';
import { heldPositionIds, type Position } from '../../types/position';
import {
  daysUntilCalendarDate,
  formatCalendarDate,
  formatInstantDay,
  parseCalendarDate,
  toCalendarDateString,
  todayCalendarDate,
} from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';

/**
 * Seeds the edit form's DatePicker from a stored calendar day.
 *
 * `x.split('T')[0]` assumed an ISO timestamp. A Postgres-style value —
 * '2026-04-12 00:00:00+00', space-separated — has no 'T', passed through whole,
 * and DatePicker fell back to `new Date(str)`: UTC midnight, the 11th in Alberta.
 * parseCalendarDate splits on 'T' OR a space and builds the day locally.
 *
 * An unreadable value is returned as it arrived, not as '': the whole form is
 * PATCHed on save, and '' would clear a stored value the user never touched.
 */
function toDateFieldValue(value: string | null | undefined): string {
  if (!value) return '';
  const day = parseCalendarDate(value);
  return day ? toCalendarDateString(day) : value;
}

function formatPhone(raw?: string | null): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

/** Line item type vocabulary comes from the canonical shared constant. */
const LINE_ITEM_LABELS = LINE_ITEM_TYPE_LABELS;

type ProfileTab = 'info' | 'certs' | 'pay' | 'timesheets';

function OnlineTrainingCerts({ employeeId }: { employeeId: string }) {
  const { data: rows, isLoading } = useQuery<any[]>({
    queryKey: ['training-certifications', employeeId],
    queryFn: () =>
      apiClient.get('/safety/training/certifications', { params: { employee_id: employeeId } })
        .then((r: any) => {
          const raw = r.data?.data ?? r.data;
          return Array.isArray(raw) ? raw : [];
        }),
    enabled: !!employeeId,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-fieldiq-muted uppercase tracking-wider mb-3">Online Training Certifications</h3>
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-6 text-center">
          <Loader2 className="w-5 h-5 text-fieldiq-muted mx-auto animate-spin" />
        </div>
      </div>
    );
  }

  if (!rows || rows.length === 0) return null;

  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold text-fieldiq-muted uppercase tracking-wider mb-3">Online Training Certifications</h3>
      <div className="space-y-3">
        {rows.map((row: any) => {
          const status = row.status;
          const isValid = status === 'active';
          const isExpiring = status === 'expiring_soon';
          const isExpired = status === 'expired';

          return (
            <div
              key={`${row.course_id}-${row.certification_id || 'none'}`}
              className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-bold text-white">{row.course_title}</h4>
                  {row.certificate_number && (
                    <p className="text-sm text-fieldiq-muted mt-0.5">#{row.certificate_number}</p>
                  )}
                  {isValid && row.expires_at && (
                    <p className="text-sm text-fieldiq-muted mt-0.5">
                      Expires: {formatCalendarDate(row.expires_at, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                  {isExpiring && row.days_until_expiry != null && (
                    <p className="text-sm text-fieldiq-muted mt-0.5">
                      Expires in {row.days_until_expiry} days
                    </p>
                  )}
                </div>
                <div>
                  {isValid ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-400 bg-green-500/10 border border-green-500/30 px-2.5 py-1 rounded-full">
                      <CheckCircle size={12} />
                      Valid
                    </span>
                  ) : isExpiring ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full">
                      <AlertTriangle size={12} />
                      Expiring Soon
                    </span>
                  ) : isExpired ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/30 px-2.5 py-1 rounded-full">
                      <AlertCircle size={12} />
                      Expired
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 bg-gray-500/10 border border-gray-500/30 px-2.5 py-1 rounded-full">
                      Never
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * "Can work as" — every position the hand holds (user_positions, API 0425).
 *
 * THE PRIMARY IS LOCKED ON. The API refuses a set without it (400), so an
 * unticked primary would be a control that fails on Save; change the primary
 * with the select above instead. Offered: active positions, plus any
 * deactivated one already held, so an existing set stays saveable.
 */
function CanWorkAsField({
  positions,
  primaryId,
  value,
  onChange,
}: {
  positions: Position[];
  primaryId: string;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const offered = positions.filter((p) => p.isActive || value.includes(p.id) || p.id === primaryId);
  // No primary check here: the disabled checkbox below is the one lock, so it is testable.
  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };
  return (
    <fieldset>
      <legend className="block text-xs text-fieldiq-muted mb-1">Can work as</legend>
      {offered.length === 0 ? (
        <p className="text-xs text-fieldiq-muted">No positions set up yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          {offered.map((p) => {
            const isPrimary = p.id === primaryId;
            return (
              <label key={p.id} className="flex items-center gap-2 text-sm text-fieldiq-text">
                <input
                  type="checkbox"
                  checked={isPrimary || value.includes(p.id)}
                  disabled={isPrimary}
                  onChange={() => toggle(p.id)}
                  className="accent-fieldiq-accent"
                />
                <span>
                  {p.name}
                  {isPrimary && <span className="text-xs text-fieldiq-muted"> (primary)</span>}
                  {!p.isActive && <span className="text-xs text-fieldiq-muted"> (deactivated)</span>}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

export const EmployeeProfile: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<ProfileTab>('info');
  const [showAddCert, setShowAddCert] = useState(false);
  const [editingCert, setEditingCert] = useState<any>(null);
  const [showEditPayRates, setShowEditPayRates] = useState(false);
  const [statusModal, setStatusModal] = useState<'inactive' | 'terminated' | 'active' | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [newPositionName, setNewPositionName] = useState('');
  const [showNewPositionInput, setShowNewPositionInput] = useState(false);
  const { isOilfieldTrucking } = useIndustry();
  // Edit, invite, certs and status/delete are four different API gates.
  // See useTeamPermissions.
  const perms = useTeamPermissions();

  const { data: employee, isLoading, error } = useQuery({
    queryKey: ['user', employeeId],
    queryFn: () => api.users.get(employeeId!).then(r => r.data.data ?? r.data ?? null),
    enabled: !!employeeId,
  });

  // Deactivated rows included: a hand may still hold one, and the profile has
  // to name it. The pickers below offer only active ones plus what is held.
  const { data: positions = [] } = usePositions({ includeInactive: true });
  const positionById = useMemo(() => new Map(positions.map((p) => [p.id, p])), [positions]);
  const held = heldPositionIds(employee);

  const createPositionMutation = useMutation({
    mutationFn: (name: string) => api.positions.create({ name }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      const created = res.data?.data ?? res.data;
      if (created?.id) {
        setEditForm((prev: any) => ({ ...prev, positionId: created.id }));
      }
      setNewPositionName('');
      setShowNewPositionInput(false);
      showToast('Position created');
    },
    onError: (err: any) => {
      const code = err.response?.data?.code;
      if (code === 'DUPLICATE_POSITION_NAME' || err.response?.status === 409) {
        showToast('That position already exists');
      } else {
        showToast('Failed to create position');
      }
    },
  });

  /*
   * Two writes, in this order, because the API requires it:
   *
   *   1. PUT /users/:id sets the PRIMARY (positionId). A new primary joins the
   *      held set there; the old one stays held.
   *   2. PUT /users/:id/positions REPLACES the held set. It refuses a set
   *      without the current primary, so it has to run after (1) — and it is
   *      sent WHOLE, primary first, because anything left out is removed.
   *
   * (2) is skipped when the set did not change. `canWorkAs` is never sent to
   * PUT /users/:id.
   */
  const updateMutation = useMutation({
    mutationFn: async (formData: any) => {
      const { canWorkAs, canWorkAsInitial, ...fields } = formData;
      await api.users.update(employeeId!, fields);
      if (Array.isArray(canWorkAs)) {
        const primary: string = fields.positionId || '';
        const full: string[] = Array.from(new Set([...(primary ? [primary] : []), ...canWorkAs]));
        const before = new Set<string>(canWorkAsInitial ?? []);
        const changed = full.length !== before.size || full.some((id) => !before.has(id));
        if (changed) await api.users.setPositions(employeeId!, full);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      setEditing(false);
      showToast('Profile updated');
    },
    onError: (err: any) => {
      // (1) may have landed before (2) failed, so refresh either way.
      queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast(getApiErrorMessage(err, 'Failed to update profile'));
    },
  });

  const resendMutation = useMutation({
    mutationFn: () => api.users.resendInvite(employeeId!),
    onSuccess: () => showToast('Invite resent successfully'),
    // Say what the API said. A page loaded before the hand accepted still shows
    // the button, and the API answers that press with 409 `already_active` and
    // a sentence naming the remedy — "Failed to resend invite" read as a fault.
    // Refetch on that code so the profile catches up and the button goes.
    onError: (err: any) => {
      showToast(getApiErrorMessage(err, 'Failed to resend invite'));
      if (err?.response?.data?.code === 'already_active') {
        queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
      }
    },
  });

  const statusMutation = useMutation({
    mutationFn: (data: { status: string; reason?: string }) => api.users.updateStatus(employeeId!, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['status-history', employeeId] });
      const label = variables.status === 'active' ? 'reactivated' : variables.status === 'inactive' ? 'set to inactive' : 'terminated';
      showToast(`Employee ${label}`);
      setStatusModal(null);
      setStatusReason('');
    },
    onError: () => {
      showToast('Failed to update status');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.users.delete(employeeId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/team', { replace: true });
    },
    onError: () => showToast('Failed to delete employee'),
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const startEdit = () => {
    setEditForm({
      firstName: employee?.firstName || '',
      lastName: employee?.lastName || '',
      email: employee?.email || '',
      phoneNumber: employee?.phoneNumber || '',
      role: employee?.role || 'field_worker',
      startDate: toDateFieldValue(employee?.startDate),
      positionId: employee?.positionId || '',
      // The held set as loaded, primary included. `undefined` from an older
      // payload means "unknown", so the control is not offered and nothing is sent.
      canWorkAs: held,
      canWorkAsInitial: held,
      address: employee?.address || '',
      city: employee?.city || '',
      provinceState: employee?.provinceState || '',
      postalZip: employee?.postalZip || '',
      dateOfBirth: toDateFieldValue(employee?.dateOfBirth),
      driversLicenceNumber: employee?.driversLicenceNumber || '',
      driversLicenceJurisdiction: employee?.driversLicenceJurisdiction || '',
      driversLicenceExpiry: toDateFieldValue(employee?.driversLicenceExpiry),
      emergencyContactName: employee?.emergencyContactName || '',
      emergencyContactPhone: employee?.emergencyContactPhone || '',
      notes: employee?.notes || '',
    });
    setEditing(true);
    setActiveTab('info');
  };

  const saveEdit = () => {
    updateMutation.mutate(editForm);
  };

  if (isLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <div className="bg-fieldiq-dark border-b border-fieldiq-border z-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/team')} className="flex items-center gap-2 text-fieldiq-accent hover:text-amber-400 transition-colors">
              <ArrowLeft size={20} strokeWidth={2.5} />
              <span className="font-semibold text-sm">Back</span>
            </button>
            <h1 className="text-base font-bold text-white">Employee Profile</h1>
          </div>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <div className="bg-fieldiq-dark border-b border-fieldiq-border z-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/team')} className="flex items-center gap-2 text-fieldiq-accent hover:text-amber-400 transition-colors">
              <ArrowLeft size={20} strokeWidth={2.5} />
              <span className="font-semibold text-sm">Back</span>
            </button>
            <h1 className="text-base font-bold text-white">Employee Profile</h1>
          </div>
        </div>
        <div className="text-center py-20 text-red-400">Failed to load employee profile.</div>
      </div>
    );
  }

  const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || employee.email;
  const initials = ((employee.firstName?.[0] || '') + (employee.lastName?.[0] || '')).toUpperCase() || '?';
  const statusKey = employee.employmentStatus || 'active';
  const certs: any[] = employee.certifications || [];
  const payRates: any[] = employee.payRates || [];
  /**
   * `jobPayExemption` rides on `safeUser`, so it is on this payload for any
   * authenticated colleague — reading it needs no admin gate, only SETTING it
   * does. Null means paid from jobs and is not absence; see the constants file.
   */
  const jobPayExempt = isJobPayExempt(employee.jobPayExemption);
  const equipmentQualifications: any[] = employee.equipmentQualifications || [];

  return (
    <div className="min-h-full bg-fieldiq-dark">
      {/* Header bar */}
      <div className="bg-fieldiq-dark border-b border-fieldiq-border z-50">
        <div className="max-w-[1920px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/team')}
                className="flex items-center gap-2 text-fieldiq-accent hover:text-amber-400 transition-colors min-h-[44px] px-2 -ml-2 rounded-lg hover:bg-white/5"
              >
                <ArrowLeft size={20} strokeWidth={2.5} />
                <span className="font-semibold text-sm">Back</span>
              </button>
              <h1 className="text-base font-bold text-white">Employee Profile</h1>
            </div>
            <div className="flex items-center gap-2">
              {!perms.canEditProfile ? null : !editing ? (
                <button
                  onClick={startEdit}
                  className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-white text-sm font-medium px-4 py-2 rounded-lg hover:border-fieldiq-accent/50 transition-colors"
                >
                  <Edit3 size={14} />
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setEditing(false)}
                    className="flex items-center gap-1 text-sm text-fieldiq-text hover:text-white transition-colors px-3 py-2"
                  >
                    <X size={14} />
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors"
                  >
                    {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-[1920px] mx-auto px-4 py-6 pb-[var(--bottom-chrome)] space-y-5">
        {/* Profile section — flat layout, no gradient */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-amber-700/40 flex items-center justify-center text-amber-300 font-bold text-xl flex-shrink-0">
            {initials}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{name}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {/* Title and role are separate chips — a role must never render as a title */}
              {(employee.positionTitle || employee.positionName) && (
                <span className="text-xs font-semibold text-fieldiq-dark bg-fieldiq-accent px-2.5 py-0.5 rounded-full">
                  {employee.positionTitle || employee.positionName}
                </span>
              )}
              <span className="text-xs font-semibold text-fieldiq-text bg-fieldiq-dark px-2.5 py-0.5 rounded-full">
                {getRoleLabel(employee.role)}
              </span>
              <span className={`flex items-center gap-1 text-sm font-medium ${statusKey === 'active' ? 'text-green-400' : statusKey === 'inactive' ? 'text-amber-400' : 'text-red-400'}`}>
                <span className={`w-2 h-2 rounded-full ${statusKey === 'active' ? 'bg-green-400' : statusKey === 'inactive' ? 'bg-amber-400' : 'bg-red-400'}`} />
                {statusKey.charAt(0).toUpperCase() + statusKey.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Segmented Tab Bar */}
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-1 grid grid-cols-4">
          {(['info', 'certs', 'pay', 'timesheets'] as ProfileTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab
                  ? 'bg-fieldiq-dark text-white shadow-sm'
                  : 'text-fieldiq-muted hover:text-fieldiq-text'
              }`}
            >
              {{ info: 'Info', certs: 'Certs', pay: 'Pay', timesheets: 'Timesheets' }[tab]}
            </button>
          ))}
        </div>

        {/* ─── Info Tab ─── */}
        {activeTab === 'info' && (
          <div className="space-y-5">
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
              {editing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="First Name" value={editForm.firstName} onChange={v => setEditForm({ ...editForm, firstName: v })} />
                    <EditField label="Last Name" value={editForm.lastName} onChange={v => setEditForm({ ...editForm, lastName: v })} />
                  </div>
                  <EditField label="Email" value={editForm.email} onChange={v => setEditForm({ ...editForm, email: v })} type="email" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-fieldiq-muted mb-1">Phone</label>
                      <input
                        type="tel"
                        value={editForm.phoneNumber}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '');
                          let formatted = '';
                          if (digits.length <= 3) formatted = digits.length ? `(${digits}` : '';
                          else if (digits.length <= 6) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                          else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
                          setEditForm({ ...editForm, phoneNumber: formatted });
                        }}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                        maxLength={14}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-fieldiq-muted mb-1">Role</label>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                      >
                        {ALL_TENANT_ROLES.map(r => (
                          <option key={r} value={r}>{getRoleLabel(r)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="Start Date" value={editForm.startDate} onChange={v => setEditForm({ ...editForm, startDate: v })} type="date" />
                    <div>
                      <label htmlFor="primary-position" className="block text-xs text-fieldiq-muted mb-1">Primary position</label>
                      <select
                        id="primary-position"
                        value={editForm.positionId}
                        onChange={(e) => {
                          const positionId = e.target.value;
                          // The new primary is held by definition; the old one stays
                          // held (as the API does) until it is unticked below.
                          setEditForm({
                            ...editForm,
                            positionId,
                            canWorkAs: Array.isArray(editForm.canWorkAs) && positionId && !editForm.canWorkAs.includes(positionId)
                              ? [...editForm.canWorkAs, positionId]
                              : editForm.canWorkAs,
                          });
                        }}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                      >
                        <option value="">Select position...</option>
                        {positions
                          .filter((p) => p.isActive || p.id === editForm.positionId)
                          .map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                      </select>
                      {!showNewPositionInput ? (
                        <button
                          type="button"
                          onClick={() => setShowNewPositionInput(true)}
                          className="flex items-center gap-1 text-xs text-fieldiq-accent hover:text-amber-400 mt-1.5 transition-colors"
                        >
                          <Plus size={12} /> Add new position
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 mt-1.5">
                          <input
                            type="text"
                            value={newPositionName}
                            onChange={(e) => setNewPositionName(e.target.value)}
                            placeholder="Position name..."
                            className="flex-1 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2.5 py-1.5 text-white text-xs focus:border-fieldiq-accent focus:outline-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (newPositionName.trim()) createPositionMutation.mutate(newPositionName.trim());
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => { if (newPositionName.trim()) createPositionMutation.mutate(newPositionName.trim()); }}
                            disabled={!newPositionName.trim() || createPositionMutation.isPending}
                            className="text-fieldiq-accent hover:text-amber-400 disabled:opacity-40 transition-colors"
                          >
                            {createPositionMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowNewPositionInput(false); setNewPositionName(''); }}
                            className="text-fieldiq-muted hover:text-white transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {Array.isArray(editForm.canWorkAs) && (
                    <CanWorkAsField
                      positions={positions}
                      primaryId={editForm.positionId || ''}
                      value={editForm.canWorkAs}
                      onChange={(canWorkAs) => setEditForm({ ...editForm, canWorkAs })}
                    />
                  )}
                  <EditField label="Address" value={editForm.address} onChange={v => setEditForm({ ...editForm, address: v })} />
                  <div className="grid grid-cols-3 gap-3">
                    <EditField label="City" value={editForm.city} onChange={v => setEditForm({ ...editForm, city: v })} />
                    <div>
                      <label className="block text-xs text-fieldiq-muted mb-1">Province/State</label>
                      <ProvinceSelect value={editForm.provinceState} onChange={v => setEditForm({ ...editForm, provinceState: v })} />
                    </div>
                    <EditField label="Postal/Zip" value={editForm.postalZip} onChange={v => setEditForm({ ...editForm, postalZip: v })} />
                  </div>
                  <EditField label="Date of Birth" value={editForm.dateOfBirth} onChange={v => setEditForm({ ...editForm, dateOfBirth: v })} type="date" />

                  {/* Driver's Licence */}
                  <div className="pt-3 mt-3 border-t border-fieldiq-border">
                    <p className="text-xs text-fieldiq-muted mb-2 font-semibold">Driver's Licence</p>
                    <div className="grid grid-cols-3 gap-3">
                      <EditField label="DL Number" value={editForm.driversLicenceNumber} onChange={v => setEditForm({ ...editForm, driversLicenceNumber: v })} />
                      <div>
                        <label className="block text-xs text-fieldiq-muted mb-1">Jurisdiction</label>
                        <ProvinceSelect value={editForm.driversLicenceJurisdiction} onChange={v => setEditForm({ ...editForm, driversLicenceJurisdiction: v })} />
                      </div>
                      <EditField label="Expiry" value={editForm.driversLicenceExpiry} onChange={v => setEditForm({ ...editForm, driversLicenceExpiry: v })} type="date" />
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="pt-3 mt-3 border-t border-fieldiq-border">
                    <p className="text-xs text-fieldiq-muted mb-2 font-semibold">Emergency Contact</p>
                    <div className="grid grid-cols-2 gap-3">
                      <EditField label="Contact Name" value={editForm.emergencyContactName} onChange={v => setEditForm({ ...editForm, emergencyContactName: v })} />
                      <div>
                        <label className="block text-xs text-fieldiq-muted mb-1">Contact Phone</label>
                        <input
                          type="tel"
                          value={editForm.emergencyContactPhone}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '');
                            let formatted = '';
                            if (digits.length <= 3) formatted = digits.length ? `(${digits}` : '';
                            else if (digits.length <= 6) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                            else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
                            setEditForm({ ...editForm, emergencyContactPhone: formatted });
                          }}
                          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                          maxLength={14}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>

                  <EditField label="Notes" value={editForm.notes} onChange={v => setEditForm({ ...editForm, notes: v })} textarea />
                </div>
              ) : (
                <div className="space-y-0">
                  <InfoRow icon={Calendar} label="Start Date" value={formatCalendarDate(employee.startDate, { month: 'long', day: 'numeric', year: 'numeric' }) ?? '—'} />
                  <InfoRow icon={User} label="Initials" value={`${initials} (used on tickets)`} />
                  {employee.positionName && <InfoRow icon={Wrench} label="Primary position" value={employee.positionName} />}
                  {held && held.length > 0 && (
                    <InfoRow
                      icon={Wrench}
                      label="Can work as"
                      // An id this list cannot name renders as a dash, never as a guessed name.
                      value={held.map((id) => positionById.get(id)?.name ?? '—').join(', ')}
                    />
                  )}
                  <InfoRow icon={Mail} label="Email" value={employee.email || '—'} />
                  <InfoRow icon={Phone} label="Phone" value={employee.phoneNumber ? formatPhone(employee.phoneNumber) : '—'} />
                  {(employee.address || employee.city) && (
                    <InfoRow icon={Mail} label="Address" value={[employee.address, employee.city, employee.provinceState, employee.postalZip].filter(Boolean).join(', ') || '—'} />
                  )}
                  {employee.dateOfBirth && <InfoRow icon={Calendar} label="Date of Birth" value={formatCalendarDate(employee.dateOfBirth, { month: 'long', day: 'numeric', year: 'numeric' }) ?? '—'} />}

                  {/* Driver's Licence */}
                  {(employee.driversLicenceNumber || employee.driversLicenceExpiry) && (() => {
                    // Same calendar-day arithmetic as the certification badge above.
                    // `driversLicenceExpiry` is camelCase, so the lint vocabulary's
                    // lowercase `expir` clause never matched it and this site went
                    // uncounted through three passes over this rule — see the
                    // widening note in eslint.legacy-patterns.js.
                    const dlDaysLeft = daysUntilCalendarDate(employee.driversLicenceExpiry, timeZone);
                    const dlExpired = dlDaysLeft !== null && dlDaysLeft < 0;
                    const dlExpiring = dlDaysLeft !== null && !dlExpired && dlDaysLeft <= 90;
                    return (
                      <div className="pt-4 mt-4 border-t border-fieldiq-border">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-fieldiq-muted">Driver's Licence</p>
                          {dlExpired && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/30 px-2.5 py-0.5 rounded-full">
                              <AlertCircle size={12} /> Expired
                            </span>
                          )}
                          {dlExpiring && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-0.5 rounded-full">
                              <AlertTriangle size={12} /> {dlDaysLeft} days
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white font-medium">
                          {employee.driversLicenceNumber || '—'}
                          {employee.driversLicenceJurisdiction ? ` (${employee.driversLicenceJurisdiction})` : ''}
                        </p>
                        {employee.driversLicenceExpiry && (
                          <p className={`text-xs mt-0.5 ${dlExpired ? 'text-red-400' : dlExpiring ? 'text-amber-400' : 'text-fieldiq-muted'}`}>
                            Expires: {formatCalendarDate(employee.driversLicenceExpiry, { month: 'long', day: 'numeric', year: 'numeric' }) ?? '—'}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Emergency Contact */}
                  <div className="pt-4 mt-4 border-t border-fieldiq-border">
                    <p className="text-xs text-fieldiq-muted mb-1">Emergency Contact</p>
                    <p className="text-sm text-white font-medium">
                      {employee.emergencyContactName || employee.emergencyContact || '—'}
                    </p>
                    <p className="text-xs text-fieldiq-muted mt-0.5">
                      {employee.emergencyContactPhone ? formatPhone(employee.emergencyContactPhone) : '—'}
                    </p>
                  </div>

                  {/* Notes */}
                  {employee.notes && (
                    <div className="pt-4 mt-4 border-t border-fieldiq-border">
                      <p className="text-xs text-fieldiq-muted mb-1">Notes</p>
                      <p className="text-sm text-white">{employee.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Status History */}
            {!editing && <StatusHistoryPanel employeeId={employeeId!} />}

            {/* Equipment Qualified For */}
            {!editing && equipmentQualifications.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Wrench size={16} className="text-fieldiq-accent" />
                  <h3 className="text-sm font-bold text-white">Equipment Qualified For</h3>
                </div>
                <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
                  {equipmentQualifications.map((eq: any, i: number) => (
                    <div key={i} className={`flex items-center gap-3 px-5 py-3 ${i < equipmentQualifications.length - 1 ? 'border-b border-fieldiq-border' : ''}`}>
                      {eq.qualified !== false ? (
                        <CheckCircle size={16} className="text-green-400 flex-shrink-0" />
                      ) : (
                        <XCircle size={16} className="text-red-400 flex-shrink-0" />
                      )}
                      <span className="text-sm text-white font-medium">{eq.name || eq.equipmentType || 'Equipment'}</span>
                      {eq.qualified !== false ? (
                        <span className="text-xs bg-fieldiq-dark border border-fieldiq-border text-fieldiq-text px-2 py-0.5 rounded-md">
                          {eq.role || getRoleLabel(employee.role) || 'Operator'}
                        </span>
                      ) : (
                        <span className="text-xs text-red-400 font-medium">Not qualified</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!editing && (
              <div className="space-y-3">
                {/* Resend Invite — never for someone who has already accepted.
                    POST /users/:id/invite stamps invite_status back to
                    'invited' whatever the current value, and /auth/login refuses
                    'invited' with the same "Invalid credentials" as a wrong
                    password — so one press on an accepted hand locked them out
                    of sign-in until they set a password again (Dash, 2026-11-02).
                    Only 'active' hides it: an absent or unknown status keeps
                    the button, so the screen never guesses the invite went in. */}
                {perms.canInvite && (employee.inviteStatus ?? employee.invite_status) !== 'active' && (
                <button
                  onClick={() => resendMutation.mutate()}
                  disabled={resendMutation.isPending}
                  className="w-full bg-fieldiq-card border border-fieldiq-border text-white py-3 rounded-xl text-sm font-medium hover:border-fieldiq-accent transition-colors flex items-center justify-center gap-2"
                >
                  {resendMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Resend Invite
                </button>
                )}

                {/* Status-aware action bar */}
                {perms.canChangeStatus && (
                <div className="flex gap-3">
                  {statusKey === 'active' && (
                    <>
                      <button
                        onClick={() => { setStatusReason(''); setStatusModal('inactive'); }}
                        className="flex-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 py-3 rounded-xl text-sm font-medium hover:bg-amber-500/20 transition-colors flex items-center justify-center gap-2"
                      >
                        <Pause size={14} />
                        Set Inactive
                      </button>
                      <button
                        onClick={() => { setStatusReason(''); setStatusModal('terminated'); }}
                        className="bg-red-500/10 border border-red-500/30 text-red-400 px-5 py-3 rounded-xl text-sm font-medium hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
                      >
                        <Trash2 size={14} />
                        Terminate
                      </button>
                    </>
                  )}
                  {statusKey === 'inactive' && (
                    <>
                      <button
                        onClick={() => { setStatusReason(''); setStatusModal('active'); }}
                        className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-3 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors flex items-center justify-center gap-2"
                      >
                        <RotateCcw size={14} />
                        Reactivate
                      </button>
                      <button
                        onClick={() => { setStatusReason(''); setStatusModal('terminated'); }}
                        className="bg-red-500/10 border border-red-500/30 text-red-400 px-5 py-3 rounded-xl text-sm font-medium hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
                      >
                        <Trash2 size={14} />
                        Terminate
                      </button>
                    </>
                  )}
                  {statusKey === 'terminated' && (
                    <button
                      onClick={() => { setStatusReason(''); setStatusModal('active'); }}
                      className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-3 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw size={14} />
                      Reactivate
                    </button>
                  )}
                </div>
                )}
                {!perms.canChangeStatus && (
                  <p data-testid="profile-readonly-note" className="text-xs text-fieldiq-muted">
                    Only an admin can edit, deactivate, terminate or delete a team member.
                  </p>
                )}
              </div>
            )}

            {/* Delete Button */}
            {!editing && perms.canDelete && (
              <button
                onClick={() => {
                  if (window.confirm(`Permanently delete ${name}? This action cannot be undone.`)) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="w-full bg-red-500/10 border border-red-500/30 text-red-400 py-3 rounded-xl text-sm font-medium hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
              >
                {deleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                Delete Employee
              </button>
            )}
          </div>
        )}

        {/* ─── Certs Tab ─── */}
        {activeTab === 'certs' && (
          <div className="space-y-3">
            {certs.length === 0 ? (
              <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-8 text-center">
                <Shield className="w-10 h-10 text-fieldiq-muted mx-auto mb-3" />
                <p className="text-fieldiq-text text-sm">No certifications on file</p>
              </div>
            ) : (
              certs.map((cert: any, i: number) => {
                // ARITHMETIC on a calendar day, so daysUntilCalendarDate — not
                // formatCalendarDate. The removed getDaysUntilExpiry() helper did
                // Math.ceil((new Date(expiry) - new Date()) / 86400000): the expiry
                // parsed to UTC midnight while `now` carried a time of day, so the
                // subtraction straddled a boundary and a cert expiring TODAY read as
                // expired (or as "1 day") purely depending on the hour it was viewed.
                // daysUntilCalendarDate floors both sides to local midnight, so today
                // is 0. Note the "Expires:" label below already used
                // formatCalendarDate — the label was right while this badge was wrong.
                const daysLeft = daysUntilCalendarDate(cert.expiryDate, timeZone);
                const isExpired = daysLeft !== null && daysLeft < 0;
                const isExpiring = daysLeft !== null && !isExpired && daysLeft <= 90;
                const isCurrent = daysLeft === null || (!isExpired && !isExpiring);
                const certLabel = CERT_TYPE_LABELS[cert.certificationType] || CERT_TYPE_LABELS[cert.type] || cert.name || cert.certificationType || cert.type || 'Unknown';

                return (
                  <div
                    key={i}
                    onClick={perms.canManageCerts ? () => setEditingCert(cert) : undefined}
                    className={`bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 ${perms.canManageCerts ? 'cursor-pointer hover:border-fieldiq-accent/30 active:scale-[0.99] transition-all' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-bold text-white">{certLabel}</h4>
                        <p className="text-sm text-fieldiq-muted mt-0.5">
                          {(cert.certificateNumber || cert.certNumber) && `#${cert.certificateNumber || cert.certNumber}`}
                          {(cert.certificateNumber || cert.certNumber) && cert.issuingBody && ' · '}
                          {cert.issuingBody}
                        </p>
                        {cert.expiryDate && (
                          <p className="text-sm text-fieldiq-muted mt-0.5">
                            Expires: {formatCalendarDate(cert.expiryDate, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isExpired ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/30 px-2.5 py-1 rounded-full">
                            <AlertCircle size={12} />
                            Expired
                          </span>
                        ) : isExpiring ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full">
                            <AlertTriangle size={12} />
                            {daysLeft} days
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-400 bg-green-500/10 border border-green-500/30 px-2.5 py-1 rounded-full">
                            <CheckCircle size={12} />
                            Current
                          </span>
                        )}
                        {perms.canManageCerts && <ChevronRight size={16} className="text-fieldiq-muted" />}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {perms.canManageCerts ? (
            <button
              onClick={() => setShowAddCert(true)}
              className="w-full bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted py-3 rounded-xl text-sm font-medium hover:border-fieldiq-accent/50 hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={14} />
              Add Certification
            </button>
            ) : (
              <p data-testid="certs-readonly-note" className="text-xs text-fieldiq-muted">
                Only an admin or office staff can add or change certifications.
              </p>
            )}

            <OnlineTrainingCerts employeeId={employeeId!} />

            {showAddCert && (
              <AddCertModal
                employeeId={employeeId!}
                onClose={() => setShowAddCert(false)}
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
                  queryClient.invalidateQueries({ queryKey: ['users'] });
                  setShowAddCert(false);
                  showToast('Certification added');
                }}
              />
            )}

            {editingCert && (
              <EditCertModal
                employeeId={employeeId!}
                cert={editingCert}
                onClose={() => setEditingCert(null)}
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
                  queryClient.invalidateQueries({ queryKey: ['users'] });
                  setEditingCert(null);
                  showToast('Certification updated');
                }}
                onDelete={() => {
                  queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
                  queryClient.invalidateQueries({ queryKey: ['users'] });
                  setEditingCert(null);
                  showToast('Certification removed');
                }}
              />
            )}
          </div>
        )}

        {/* ─── Pay Tab ─── */}
        {activeTab === 'pay' && (
          <div className="space-y-4">
            {/*
              Rendered above BOTH branches on purpose: how a worker is paid is a
              fact about the worker, not about the tenant's vertical, and
              previewShopPay reads it whichever branch renders underneath.
            */}
            <JobPayArrangementSection
              employeeId={employeeId!}
              jobPayExemption={employee.jobPayExemption}
              hasPayRates={payRates.length > 0}
              onToast={showToast}
            />

            <ExpenseReimbursementSection
              employeeId={employeeId!}
              stored={(employee as any).expenseReimbursement ?? null}
              resolved={(employee as any).expenseReimbursementResolved ?? null}
              onToast={showToast}
            />

            {isOilfieldTrucking ? (
              <PayProfileTab userId={employeeId!} onToast={showToast} />
            ) : (
            <div className="space-y-4">
              {payRates.length === 0 ? (
                /*
                  ITEM 1. "No pay rates configured" is a to-do: it tells the
                  office to go and fix something. For an exempt worker there is
                  nothing to fix — a tenant admin deliberately took them off job
                  pay — so the sentence is the wrong one, not merely a blunt one.
                  The arrangement above says what IS true, and this says nothing
                  rather than saying something false.
                */
                jobPayExempt ? null : (
                <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-8 text-center">
                  <DollarSign className="w-10 h-10 text-fieldiq-muted mx-auto mb-3" />
                  <p className="text-fieldiq-text text-sm">No pay rates configured</p>
                </div>
                )
              ) : (
                <>
                  <p className="text-sm text-fieldiq-muted">{initials} — Pay Rates (CAD)</p>
                  <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
                    {/* Scrolls instead of crushing, like every register. The header
                        and each row are separate seven-track grids that only line
                        up because the tracks may shrink below their content; below
                        ~640px a label like "Removing Caps" then ran into the next
                        column's value, so the floor keeps every cell legible. */}
                    <div className="overflow-x-auto">
                    <div className="min-w-[640px]">
                    <div className="grid grid-cols-7 px-5 py-3 border-b border-fieldiq-border bg-fieldiq-dark/30">
                      <span className="text-xs font-semibold text-fieldiq-accent">Line Item</span>
                      <span className="text-xs font-semibold text-fieldiq-accent text-center">Single%</span>
                      <span className="text-xs font-semibold text-fieldiq-accent text-center">Dual%</span>
                      <span className="text-xs font-semibold text-fieldiq-accent text-center">Triple%</span>
                      <span className="text-xs font-semibold text-fieldiq-accent text-center">Quad%</span>
                      <span className="text-xs font-semibold text-fieldiq-accent text-center">Day $</span>
                      <span className="text-xs font-semibold text-fieldiq-accent text-center">Hr $</span>
                    </div>
                    {payRates.map((rate: any, i: number) => {
                      const lineType = rate.lineItemType || rate.line_item_type || '';
                      const itemLabel = getLineItemTypeLabel(lineType) || 'General';
                      // A stored zero used to render "0%" here while `fmtDollar`
                      // ten lines down rendered '—' for the same zero, in the
                      // same row. Both cells mean "unset" by a zero, and now
                      // both say so. `maxDecimals: 2` keeps a configured 11.6%
                      // from rounding to 12% — this sheet shows the rate itself,
                      // where the pay stub shows whole percent.
                      const fmtPct = (v: any) => fmtPayPercentage(v, { maxDecimals: 2, absent: '—' });
                      // A pay-rate column is optional: a tech paid on percentage has no
                      // day rate, and the API sends 0 for that, so 0 means UNSET here and
                      // stays an em dash. That is a local convention about this grid's
                      // zeros, not a second opinion on how money is formatted — the
                      // formatting is fmtCurrency's, which this file already imports.
                      const fmtDollar = (v: any) =>
                        v == null || v === '' || Number(v) === 0 ? '—' : fmtCurrency(v);
                      const singleVal = rate.singleTechPercentage ?? rate.single_tech_percentage;
                      const dualVal = rate.dualTechPercentage ?? rate.dual_tech_percentage;
                      const tripleVal = rate.tripleTechPercentage ?? rate.triple_tech_percentage;
                      const quadVal = rate.quadTechPercentage ?? rate.quad_tech_percentage;
                      const dayVal = rate.dayRateAmount ?? rate.day_rate_amount;
                      const hourlyVal = rate.hourlyRateAmount ?? rate.hourly_rate_amount;
                      // The Shop row's overtime switch (API 0384), shown so the
                      // office can see who is on which without opening the
                      // editor. Strict: only a literal true reads as on.
                      const shopOt = lineType === 'shop'
                        ? ((rate.overtimeEligible ?? rate.overtime_eligible) === true)
                        : null;
                      return (
                        <div key={i} className={`grid grid-cols-7 px-5 py-3.5 ${i < payRates.length - 1 ? 'border-b border-fieldiq-border' : ''}`}>
                          <span className="text-sm text-white font-medium capitalize flex items-center gap-2">
                            {itemLabel}
                            {shopOt !== null && (
                              <span
                                data-testid="pay-rate-shop-ot-badge"
                                title={shopOt ? 'Shop hours count toward overtime for this worker' : 'Shop hours never earn overtime for this worker'}
                                className={`normal-case shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded px-1 py-px border ${shopOt ? 'text-emerald-400 border-emerald-400/40' : 'text-fieldiq-muted border-fieldiq-border'}`}
                              >
                                {shopOt ? 'OT eligible' : 'No OT'}
                              </span>
                            )}
                          </span>
                          <span className="text-sm text-white text-center">{fmtPct(singleVal)}</span>
                          <span className="text-sm text-white text-center">{fmtPct(dualVal)}</span>
                          <span className="text-sm text-white text-center">{fmtPct(tripleVal)}</span>
                          <span className="text-sm text-white text-center">{fmtPct(quadVal)}</span>
                          <span className="text-sm text-white text-center">{fmtDollar(dayVal)}</span>
                          <span className="text-sm text-white text-center">{fmtDollar(hourlyVal)}</span>
                        </div>
                      );
                    })}
                    </div>
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={() => setShowEditPayRates(true)}
                className="w-full bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted py-3 rounded-xl text-sm font-medium hover:border-fieldiq-accent/50 hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <Edit3 size={14} />
                Edit Pay Rates
              </button>

              {showEditPayRates && (
                <EditPayRatesModal
                  employeeId={employeeId!}
                  payRates={payRates}
                  onClose={() => setShowEditPayRates(false)}
                  onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
                    setShowEditPayRates(false);
                    showToast('Pay rates updated');
                  }}
                />
              )}
            </div>
            )}
          </div>
        )}

        {/* ─── Timesheets Tab ─── */}
        {activeTab === 'timesheets' && (
          <TimesheetsTab userId={employeeId!} />
        )}

      </main>

      {/* Set Inactive Modal */}
      <ConfirmModal
        isOpen={statusModal === 'inactive'}
        title="Set Inactive"
        body={
          <div className="space-y-3">
            <p>{name} will be hidden from active rosters, dispatch, and scheduling. All their info, certs, and history are kept and can be restored anytime.</p>
            <div>
              <label className="block text-xs text-fieldiq-muted mb-1">Reason (optional)</label>
              <textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                rows={2}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none resize-none"
                placeholder="e.g. Seasonal layoff, leave of absence..."
              />
            </div>
          </div>
        }
        confirmLabel="Set Inactive"
        confirmVariant="primary"
        loading={statusMutation.isPending}
        onConfirm={() => statusMutation.mutate({ status: 'inactive', reason: statusReason || undefined })}
        onCancel={() => { setStatusModal(null); setStatusReason(''); }}
      />

      {/* Terminate Modal */}
      <ConfirmModal
        isOpen={statusModal === 'terminated'}
        title="Terminate Employee"
        body={
          <div className="space-y-3">
            <p>Mark {name} as terminated. Their record and history are kept but they're removed from active rosters. You can still reactivate later.</p>
            <div>
              <label className="block text-xs text-fieldiq-muted mb-1">Reason (optional)</label>
              <textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                rows={2}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none resize-none"
                placeholder="e.g. Resigned, end of contract..."
              />
            </div>
          </div>
        }
        confirmLabel="Terminate"
        confirmVariant="destructive"
        loading={statusMutation.isPending}
        onConfirm={() => statusMutation.mutate({ status: 'terminated', reason: statusReason || undefined })}
        onCancel={() => { setStatusModal(null); setStatusReason(''); }}
      />

      {/* Reactivate Modal */}
      <ConfirmModal
        isOpen={statusModal === 'active'}
        title="Reactivate Employee"
        body={`Reactivate ${name}? They'll be restored to active rosters, dispatch, and scheduling.`}
        confirmLabel="Reactivate"
        confirmVariant="primary"
        loading={statusMutation.isPending}
        onConfirm={() => statusMutation.mutate({ status: 'active' })}
        onCancel={() => { setStatusModal(null); setStatusReason(''); }}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2.5 rounded-xl shadow-lg text-sm animate-fade-in z-50">
          {toast}
        </div>
      )}
    </div>
  );
};

/* Reusable edit field */
function EditField({ label, value, onChange, type = 'text', placeholder, textarea }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; textarea?: boolean;
}) {
  const cls = "w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none";
  return (
    <div>
      <label className="block text-xs text-fieldiq-muted mb-1">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} className={`${cls} resize-none`} placeholder={placeholder} />
      ) : type === 'date' ? (
        <DatePicker value={value} onChange={onChange} placeholder={placeholder || 'Select date'} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} className={cls} placeholder={placeholder} />
      )}
    </div>
  );
}

/* Detail row for the Info tab */
function InfoRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <Icon className="w-4 h-4 text-fieldiq-muted flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-fieldiq-muted">{label}</p>
        <p className="text-sm text-white font-medium">{value}</p>
      </div>
    </div>
  );
}

/* Status History Panel */
function StatusHistoryPanel({ employeeId }: { employeeId: string }) {
  const timeZone = useTenantTimeZone();
  const { data, isLoading } = useQuery<any[]>({
    queryKey: ['status-history', employeeId],
    queryFn: () =>
      apiClient.get(`/users/${employeeId}/status-history`)
        .then((r: any) => {
          const raw = r.data?.data ?? r.data;
          return Array.isArray(raw) ? raw : [];
        }),
    enabled: !!employeeId,
    staleTime: 30_000,
  });

  const STATUS_LABELS: Record<string, string> = { active: 'Active', inactive: 'Inactive', terminated: 'Terminated' };

  if (isLoading) {
    return (
      <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
        <div className="flex items-center gap-2 mb-3">
          <History size={16} className="text-fieldiq-accent" />
          <h3 className="text-sm font-bold text-white">Status History</h3>
        </div>
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 text-fieldiq-muted animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <History size={16} className="text-fieldiq-accent" />
        <h3 className="text-sm font-bold text-white">Status History</h3>
      </div>
      {!data || data.length === 0 ? (
        <p className="text-sm text-fieldiq-muted">No status changes yet.</p>
      ) : (
        <div className="space-y-2">
          {data.map((entry: any, i: number) => {
            const from = STATUS_LABELS[entry.fromStatus || entry.from_status] || entry.fromStatus || entry.from_status || '—';
            const to = STATUS_LABELS[entry.toStatus || entry.to_status] || entry.toStatus || entry.to_status || '—';
            const reason = entry.reason;
            const actor = entry.changedByName || entry.changed_by_name || entry.changedBy || entry.changed_by;
            const date = entry.createdAt || entry.created_at;
            // created_at is an instant: the TENANT's day. Absent (or no zone)
            // omits the segment below rather than guessing a day.
            const dateStr = formatInstantDay(date, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '';

            return (
              <div key={entry.id || i} className="text-sm text-fieldiq-text py-2 border-b border-fieldiq-border last:border-b-0">
                <span className="text-white font-medium">{from}</span>
                <span className="text-fieldiq-muted"> → </span>
                <span className="text-white font-medium">{to}</span>
                {reason && <span className="text-fieldiq-muted"> · '{reason}'</span>}
                {actor && <span className="text-fieldiq-muted"> · {actor}</span>}
                {dateStr && <span className="text-fieldiq-muted"> · {dateStr}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Add Certification Modal */
function AddCertModal({ employeeId, onClose, onSuccess }: { employeeId: string; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    certificationType: 'H2S_alive',
    certificateNumber: '',
    issuingBody: '',
    issuedDate: '',
    expiryDate: '',
    notes: '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data: typeof form) => api.users.addCertification(employeeId, data),
    onSuccess,
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to add certification.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.certificationType) {
      setError('Certification type is required.');
      return;
    }
    setError('');
    mutation.mutate(form);
  };

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border">
          <h3 className="text-lg font-bold text-white">Add Certification</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Type</label>
            <select
              value={form.certificationType}
              onChange={(e) => set('certificationType', e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
            >
              {CERT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Certificate #</label>
              <input
                type="text"
                value={form.certificateNumber}
                onChange={(e) => set('certificateNumber', e.target.value)}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                placeholder="12345"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Issuing Body</label>
              <input
                type="text"
                value={form.issuingBody}
                onChange={(e) => set('issuingBody', e.target.value)}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                placeholder="Enform"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Issued Date</label>
              <DatePicker value={form.issuedDate} onChange={(v) => set('issuedDate', v)} />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Expiry Date</label>
              <DatePicker value={form.expiryDate} onChange={(v) => set('expiryDate', v)} />
            </div>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Notes</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
              placeholder="Optional notes"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {mutation.isPending ? 'Adding...' : 'Add Certification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Edit Certification Modal */
function EditCertModal({ employeeId, cert, onClose, onSuccess, onDelete }: {
  employeeId: string; cert: any; onClose: () => void; onSuccess: () => void; onDelete: () => void;
}) {
  // Seeds <input type="date">. Round-tripping through toISOString() would emit
  // UTC's day, which is the day before for anything carrying an evening offset.
  const formatDate = (d: any) => {
    const dt = parseCalendarDate(d);
    return dt ? toCalendarDateString(dt) : '';
  };

  const [form, setForm] = useState({
    certificationType: cert.type || cert.certificationType || 'H2S_alive',
    certificateNumber: cert.certNumber || cert.certificateNumber || '',
    issuingBody: cert.issuingBody || '',
    issuedDate: formatDate(cert.issuedDate),
    expiryDate: formatDate(cert.expiryDate),
    notes: cert.notes || '',
  });
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docUploaded, setDocUploaded] = useState(!!cert.hasDocument);
  const [docUploadId, setDocUploadId] = useState(cert.uploadId || null);
  const fileRef = useRef<HTMLInputElement>(null);

  const updateMutation = useMutation({
    mutationFn: (data: typeof form) => api.users.updateCertification(employeeId, cert.id, data),
    onSuccess,
    onError: (err: any) => setError(err.response?.data?.error || 'Failed to update certification.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.users.deleteCertification(employeeId, cert.id),
    onSuccess: onDelete,
    onError: (err: any) => setError(err.response?.data?.error || 'Failed to delete certification.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    updateMutation.mutate(form);
  };

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border">
          <h3 className="text-lg font-bold text-white">Edit Certification</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Type</label>
            <select
              value={form.certificationType}
              onChange={(e) => set('certificationType', e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
            >
              {CERT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Certificate #</label>
              <input
                type="text"
                value={form.certificateNumber}
                onChange={(e) => set('certificateNumber', e.target.value)}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                placeholder="12345"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Issuing Body</label>
              <input
                type="text"
                value={form.issuingBody}
                onChange={(e) => set('issuingBody', e.target.value)}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                placeholder="Enform"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Issued Date</label>
              <DatePicker value={form.issuedDate} onChange={(v) => set('issuedDate', v)} />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Expiry Date</label>
              <DatePicker value={form.expiryDate} onChange={(v) => set('expiryDate', v)} />
            </div>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Notes</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
              placeholder="Optional notes"
            />
          </div>

          {/* Document Upload */}
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Document / Photo</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploading(true);
                try {
                  const res = await api.uploads.getPresignedUrl({
                    fileName: file.name,
                    contentType: file.type,
                    entityType: 'employee_cert',
                    entityId: cert.id,
                  });
                  const { uploadUrl, key } = res.data;
                  // Throws unless S3 answered 2xx — confirm/`setDocUploaded`
                  // below are unreachable for bytes that are not in the bucket.
                  await putToPresignedUrl(uploadUrl, file, file.type);
                  await api.uploads.confirm({ key, size: file.size });
                  setDocUploaded(true);
                  // Fetch the upload record to get the ID for viewing
                  const uploads = await api.uploads.listForEntity('employee_cert', cert.id);
                  const latest = uploads.data?.data?.[0];
                  if (latest) setDocUploadId(latest.id);
                } catch (err) {
                  console.error('Upload failed:', err);
                  setError(getApiErrorMessage(err, 'Failed to upload document'));
                } finally {
                  setUploading(false);
                  e.target.value = '';
                }
              }}
            />
            {docUploaded ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (docUploadId) {
                      void openUploadDownload(docUploadId);
                    }
                  }}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 py-2 rounded-lg text-sm font-medium hover:bg-green-500/20 transition-colors"
                >
                  <Eye size={14} /> View Document
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center justify-center gap-1.5 bg-fieldiq-dark border border-fieldiq-border text-fieldiq-text py-2 px-3 rounded-lg text-sm font-medium hover:border-fieldiq-accent hover:text-fieldiq-accent transition-colors disabled:opacity-50"
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Replace
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 bg-fieldiq-dark border border-dashed border-fieldiq-border text-fieldiq-text py-2.5 rounded-lg text-sm font-medium hover:border-fieldiq-accent hover:text-fieldiq-accent transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <><Loader2 size={14} className="animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={14} /> Upload Document or Photo</>
                )}
              </button>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {/* Delete */}
          <div className="pt-2 border-t border-fieldiq-border">
            {confirmDelete ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-red-400 flex-1">Delete this certification?</span>
                <button type="button" onClick={() => setConfirmDelete(false)}
                  className="text-sm text-fieldiq-muted hover:text-white px-3 py-1.5">
                  Cancel
                </button>
                <button type="button" onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="text-sm text-red-400 font-semibold bg-red-500/10 border border-red-500/30 px-3 py-1.5 rounded-lg hover:bg-red-500/20 disabled:opacity-50 flex items-center gap-1">
                  {deleteMutation.isPending && <Loader2 size={12} className="animate-spin" />}
                  Confirm Delete
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)}
                className="w-full flex items-center justify-center gap-1.5 text-sm text-red-400 hover:text-red-300 py-2 transition-colors">
                <Trash2 size={14} />
                Delete Certification
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/* Rate coverage — GET /api/v1/payroll/rate-coverage
 *
 * Reports, for the caller's company, each line_item_type in use, whether a pay
 * rate is configured for it, and how many line items are affected. Key casing
 * varies across the API surface, so normalise defensively rather than assuming
 * one spelling. */
type RateCoverageRow = {
  type: LineItemType;
  inUse: boolean;
  hasRate: boolean;
  lineItemCount: number;
};

function parseRateCoverage(payload: any): RateCoverageRow[] {
  const rows: any[] = Array.isArray(payload) ? payload
    : Array.isArray(payload?.data) ? payload.data
    : Array.isArray(payload?.coverage) ? payload.coverage
    : [];

  return rows.flatMap((r: any) => {
    const type = toLineItemType(r.lineItemType ?? r.line_item_type ?? r.type);
    if (!type) return [];
    const count = Number(
      r.lineItemCount ?? r.line_item_count ?? r.count ?? r.affectedCount ?? r.affected_count ?? 0
    );
    const hasRate = Boolean(
      r.hasRate ?? r.has_rate ?? r.hasPayRate ?? r.has_pay_rate ?? r.configured ?? r.rateConfigured
    );
    return [{
      type,
      // A row only appears when the type is in use; fall back to the count.
      inUse: r.inUse ?? r.in_use ?? (Number.isFinite(count) ? count > 0 : true),
      hasRate,
      lineItemCount: Number.isFinite(count) ? count : 0,
    }];
  });
}

/* Edit Pay Rates Modal (Casing)
 *
 * The row list, the form-state seed, and the save loop all iterate the
 * canonical LINE_ITEM_TYPES from src/constants/line-item-types.ts. Keeping a
 * local copy here is what previously made `bail_extension` and other
 * late-added types' pay rates unconfigurable — every type in the enum must be
 * editable and saveable, so do not reintroduce a hardcoded list. */

/**
 * How this worker is paid, and — for a tenant admin — the door to change it.
 *
 * ── THE READ IS UNGATED, THE WRITE IS NOT ───────────────────────────────────
 * The sentence renders for everyone who can open this profile. `jobPayExemption`
 * arrives on `safeUser` for any authenticated colleague, and the API's own
 * comment on the payroll endpoints is explicit that gating them strictly "does
 * not make the value private, and nothing downstream should assume it does".
 * Hiding the sentence from an office user would invent a secrecy rule the API
 * does not have; hiding the BUTTON matches a refusal they would really get.
 *
 * ── WHY IT RENDERS WHEN THERE IS NOTHING TO REPORT ──────────────────────────
 * "Paid from jobs" shows too, not just the exemptions. A setting that appears
 * only once it is set is one an admin cannot audit: they would have to remember
 * the feature exists to go looking for it, and the absence of a row is equally
 * consistent with "paid normally" and "this build cannot show it". One line that
 * always states the arrangement is what makes the other three trustworthy.
 */
function JobPayArrangementSection({ employeeId, jobPayExemption, hasPayRates, onToast }: {
  employeeId: string;
  jobPayExemption: unknown;
  hasPayRates: boolean;
  onToast: (msg: string) => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const mayEdit = canSetJobPayExemption(user?.role);
  const exempt = isJobPayExempt(jobPayExemption);
  const label = jobPayArrangementLabel(jobPayExemption);
  const description = isJobPayExemption(jobPayExemption)
    ? JOB_PAY_EXEMPTION_DESCRIPTIONS[jobPayExemption]
    : null;

  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <DollarSign
            className={`w-5 h-5 shrink-0 mt-0.5 ${exempt ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}
          />
          {/* Named group around the label, the value and its explanation — the
              text column only. The contradiction banner further down carries a
              literal em dash, so a name on the whole card would put it inside
              anything scoped to "the arrangement". */}
          <div className="min-w-0" role="group" aria-label="Job pay arrangement">
            <p className="text-xs text-fieldiq-muted uppercase tracking-wide">Job pay arrangement</p>
            <p className="text-sm font-bold text-white mt-1">{label}</p>
            {description && (
              <p className="text-xs text-fieldiq-muted mt-1">{description}</p>
            )}
            {exempt && (
              <p className="text-xs text-fieldiq-text mt-2">
                Their hours are still recorded. They will not appear as a missing pay
                rate on payroll reports.
              </p>
            )}
          </div>
        </div>

        {mayEdit && (
          <button
            onClick={() => setEditing(true)}
            className="bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted px-3 py-2 rounded-lg text-sm font-medium hover:border-fieldiq-accent/50 hover:text-white transition-colors flex items-center gap-2 shrink-0"
          >
            <Edit3 size={14} />
            Change
          </button>
        )}
      </div>

      {/*
        A configured rate and an exemption disagree, and the API resolves that in
        favour of the RATE — in both consumers, deliberately, because suppressing
        a rate somebody entered is a money change and the operator's call. The
        API logs the contradiction rather than silently picking; this says the
        same thing to the person who can actually settle it. Without it the
        screen shows an exemption above a grid of rates that will still pay, and
        the two read as though one of them is not real.
      */}
      {exempt && hasPayRates && (
        <div className="mt-4 bg-fieldiq-card border border-fieldiq-accent/40 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-fieldiq-accent shrink-0 mt-0.5" />
          <p className="text-xs text-fieldiq-text">
            This worker is marked as not paid from jobs, but still has pay rates
            configured below. <strong className="text-white">The rates win</strong> — job
            work will still pay. Remove the rates if the exemption is the intended
            answer.
          </p>
        </div>
      )}

      {editing && (
        <JobPayExemptionModal
          employeeId={employeeId}
          current={jobPayExemption}
          onClose={() => setEditing(false)}
          onSuccess={(saved) => {
            queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setEditing(false);
            onToast(
              isJobPayExempt(saved)
                ? `Marked as ${jobPayArrangementLabel(saved).toLowerCase()}`
                : 'Marked as paid from jobs',
            );
          }}
        />
      )}
    </div>
  );
}

/**
 * The setter.
 *
 * ── EVERY OPTION IS SENT EXPLICITLY, INCLUDING THE DEFAULT ──────────────────
 * Choosing "Paid from jobs" PUTs `{ jobPayExemption: null }`. It does not send
 * an empty body and it does not skip the call. The API refuses a body with the
 * key absent for exactly this reason: a form that omits the key on its default
 * option would clear an exemption whenever an admin opened the dialog and saved
 * without meaning to change anything — a 200, no error, and a worker quietly
 * back on job pay.
 */
function JobPayExemptionModal({ employeeId, current, onClose, onSuccess }: {
  employeeId: string;
  current: unknown;
  onClose: () => void;
  onSuccess: (saved: JobPayExemption | null) => void;
}) {
  const currentValue: JobPayExemption | null = isJobPayExemption(current) ? current : null;
  const [choice, setChoice] = useState<JobPayExemption | null>(currentValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /**
   * The stored value when this build does not recognise it — a newer API having
   * added a fourth exemption.
   *
   * It gets its own option, so the dialog opens with the worker's real setting
   * selected instead of silently showing "Paid from jobs". A radio group that
   * cannot represent the current value does not merely look wrong: saving would
   * write the option it highlighted, so an admin who opened the dialog to change
   * a phone number's worth of nothing would clear an exemption they never saw.
   */
  const unknownCurrent = isJobPayExempt(current) && !isJobPayExemption(current)
    ? String(current)
    : null;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.payroll.setJobPayExemption(employeeId, choice);
      onSuccess(choice);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to save the job pay arrangement'));
    } finally {
      setSaving(false);
    }
  };

  const options: { value: JobPayExemption | null; label: string; description: string }[] = [
    // The default is FIRST and is a real choice, not the absence of one.
    {
      value: null,
      label: JOB_PAID_LABEL,
      description: 'Paid for job work at the rates configured on this profile.',
    },
    ...JOB_PAY_EXEMPTIONS.map((value) => ({
      value,
      label: JOB_PAY_EXEMPTION_LABELS[value],
      description: JOB_PAY_EXEMPTION_DESCRIPTIONS[value],
    })),
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg shadow-2xl animate-fade-in max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border shrink-0">
          <h3 className="text-lg font-bold text-white">Job pay arrangement</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <p className="text-xs text-fieldiq-muted">
            Most workers are paid from jobs. Choose one of the others only when this
            person is deliberately paid another way — they will stop being reported as
            a missing pay rate.
          </p>

          {/*
            NOT a selectable option, because it is not one this screen can write:
            sending a value it does not recognise back to the API is how a typo
            round-trips into the database looking like a decision. It states what
            is stored so an admin is not told their setting is something else,
            and every option below replaces it.
          */}
          {unknownCurrent !== null && (
            <div className="rounded-xl border border-fieldiq-accent/40 p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-fieldiq-accent shrink-0 mt-0.5" />
              <p className="text-xs text-fieldiq-text">
                Currently set to <strong className="text-white">{unknownCurrent}</strong>, which
                this screen does not recognise — it may have been added by a newer
                version. They are not paid from jobs today. Choosing an option below
                replaces that setting.
              </p>
            </div>
          )}

          {options.map((opt) => {
            const selected = choice === opt.value;
            return (
              <label
                key={opt.value ?? 'job-paid'}
                className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 cursor-pointer transition-colors ${
                  selected
                    ? 'border-fieldiq-accent bg-fieldiq-accent/5'
                    : 'border-fieldiq-border hover:border-fieldiq-accent/50'
                }`}
              >
                <input
                  type="radio"
                  name="job-pay-arrangement"
                  className="mt-1 shrink-0"
                  checked={selected}
                  onChange={() => setChoice(opt.value)}
                />
                <span className="min-w-0">
                  <span className="text-sm font-medium text-white">{opt.label}</span>
                  <span className="block text-xs text-fieldiq-muted mt-0.5">{opt.description}</span>
                </span>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-fieldiq-border shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted px-4 py-2 rounded-lg text-sm font-medium hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-fieldiq-accent text-black px-4 py-2 rounded-lg text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function EditPayRatesModal({ employeeId, payRates, onClose, onSuccess }: {
  employeeId: string;
  payRates: any[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const buildFormState = () => {
    const state: Record<string, { single: string; dual: string; triple: string; quad: string; dayRate: string; hourly: string; overtimeEligible: boolean; id?: string }> = {};
    for (const type of LINE_ITEM_TYPES) {
      // Normalise so pre-migration rows (e.g. `casing_running`) bind to their
      // canonical row (`crt`) instead of silently showing as unconfigured.
      const existing = payRates.find((r: any) =>
        toLineItemType(r.lineItemType || r.line_item_type) === type
      );
      if (existing) {
        const singleRaw = existing.singleTechPercentage ?? existing.single_tech_percentage ?? existing.singleTechPercent ?? '';
        const dualRaw = existing.dualTechPercentage ?? existing.dual_tech_percentage ?? existing.dualTechPercent ?? '';
        const tripleRaw = existing.tripleTechPercentage ?? existing.triple_tech_percentage ?? existing.tripleTechPercent ?? '';
        const quadRaw = existing.quadTechPercentage ?? existing.quad_tech_percentage ?? '';
        const dayRaw = existing.dayRateAmount ?? existing.day_rate_amount ?? '';
        const hourlyRaw = existing.hourlyRateAmount ?? existing.hourly_rate_amount ?? '';
        // Strict: only a literal true switches it on. Anything else — absent,
        // null, a string — is off, which is the API's default and Chris's answer.
        const otRaw = (existing.overtimeEligible ?? existing.overtime_eligible) === true;
        state[type] = {
          single: singleRaw != null && singleRaw !== '' ? String(Math.round(Number(singleRaw) * 10000) / 100) : '',
          dual: dualRaw != null && dualRaw !== '' ? String(Math.round(Number(dualRaw) * 10000) / 100) : '',
          triple: tripleRaw != null && tripleRaw !== '' ? String(Math.round(Number(tripleRaw) * 10000) / 100) : '',
          quad: quadRaw != null && quadRaw !== '' ? String(Math.round(Number(quadRaw) * 10000) / 100) : '',
          dayRate: dayRaw != null && dayRaw !== '' ? String(dayRaw) : '',
          hourly: hourlyRaw != null && hourlyRaw !== '' ? String(hourlyRaw) : '',
          id: existing.id,
          overtimeEligible: otRaw,
        };
      } else {
        state[type] = { single: '', dual: '', triple: '', quad: '', dayRate: '', hourly: '', overtimeEligible: false };
      }
    }
    return state;
  };

  const [form, setForm] = useState(buildFormState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /**
   * Which line item types this company actually uses, and whether each has a
   * pay rate configured.
   *
   * ADVISORY, BUT ITS SILENCE IS NOT NEUTRAL. `{ data }` alone is two branches
   * where there are three: a refused or failed read arrives as `undefined`,
   * `?? []` makes `uncovered` empty, and an empty `uncovered` renders no banner
   * — which is the same screen a company with every rate configured gets. The
   * reassuring reading is the one a failure produces. `error` is destructured
   * so the two can be told apart below.
   */
  const coverageQuery = useQuery<RateCoverageRow[]>({
    queryKey: ['payroll', 'rate-coverage'],
    queryFn: () => api.payroll.getRateCoverage().then((r: any) => parseRateCoverage(r.data)),
    retry: false,
    staleTime: 60_000,
  });
  const coverage = coverageQuery.data;
  const coverageFailure = queryLoadFailure(coverageQuery);

  const uncovered = (coverage ?? []).filter(c => c.inUse && !c.hasRate);

  // The types this company hides (Settings → Line Item Types). A hidden type
  // drops out of the grid UNLESS this worker already has a rate on it — a
  // rate the tenant is paying must stay visible, flagged, or hiding the type
  // would also hide the money. `id` covers a stored row every field of which
  // is blank; the field checks cover one the user has typed into this session.
  const { hidden: hiddenTypes } = useHiddenLineItemTypes();
  const rowHasRate = (type: string) => {
    const row = form[type];
    if (!row) return false;
    return Boolean(row.id) || row.overtimeEligible || [row.single, row.dual, row.triple, row.quad, row.dayRate, row.hourly].some(v => v !== '');
  };
  // The coverage banner is the second exception: rate-coverage keeps reporting
  // a hidden type the company still bills (its rows carry `hidden: true`), and
  // a row the banner says needs a rate has to exist for the rate to be entered.
  const gridTypes = payRateGridTypes(
    hiddenTypes,
    type => rowHasRate(type) || uncovered.some(c => c.type === type),
  );
  const hiddenWithRate = gridTypes.filter(t => hiddenTypes.has(t));

  const updateField = (type: string, field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      for (const type of LINE_ITEM_TYPES) {
        const row = form[type];
        const hasSingle = row.single !== '';
        const hasDual = row.dual !== '';
        const hasTriple = row.triple !== '';
        const hasQuad = row.quad !== '';
        const hasDayRate = row.dayRate !== '';
        const hasHourly = row.hourly !== '';
        const hasAny = hasSingle || hasDual || hasTriple || hasQuad || hasDayRate || hasHourly;

        if (!hasAny && !row.id) continue;

        if (!hasAny && row.id) {
          await api.payroll.deletePayRate(row.id);
          continue;
        }

        await api.payroll.savePayRate({
          userId: employeeId,
          lineItemType: type,
          singleTechPercentage: hasSingle ? parseFloat(row.single) / 100 : null,
          dualTechPercentage: hasDual ? parseFloat(row.dual) / 100 : null,
          tripleTechPercentage: hasTriple ? parseFloat(row.triple) / 100 : null,
          quadTechPercentage: hasQuad ? parseFloat(row.quad) / 100 : null,
          dayRateAmount: hasDayRate ? parseFloat(row.dayRate) : null,
          hourlyRateAmount: hasHourly ? parseFloat(row.hourly) : null,
          // Sent on EVERY row, as a boolean. The API reads an absent key as
          // false, so sending it explicitly is what makes "unticked" a fact
          // the form asserts rather than one it forgot to mention.
          overtimeEligible: row.overtimeEligible === true,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save pay rates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-2xl shadow-2xl animate-fade-in max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border shrink-0">
          <h3 className="text-lg font-bold text-white">Edit Pay Rates</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {/* Unconfigured pay rate warning */}
          {/* A FAILED COVERAGE READ IS NOT "EVERY RATE IS SET". It takes the
              place the banner would have had, so the reader is never shown a
              clean modal that was actually a failed request. It is one line and
              carries no retry: a refusal is an answer, and the rates below are
              still editable either way, which is why this does not block the
              form. */}
          {coverageFailure && (
            <LoadFailureNotice
              error={coverageQuery.error}
              kind={coverageFailure}
              subject="which line item types need a pay rate"
              className="mb-4"
            />
          )}

          {!coverageFailure && uncovered.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3 mb-4">
              <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-400">
                  {uncovered.length} line item type{uncovered.length !== 1 ? 's' : ''} in use with no pay rate configured
                </p>
                {/* Copy corrected alongside fieldiq-api#352. It used to read
                    "fall through to the AI-guess pay path", which stopped being
                    true when payability became a (tenant, type) fact: a type
                    with no rate is now skipped by the deterministic matcher AND
                    kept out of the AI work list, so it produces nothing at all.
                    The old wording read as "it will be handled approximately",
                    which is reassuring and the opposite of the action needed —
                    setting a rate here is the only thing that makes these lines
                    generate pay. */}
                <p className="text-xs text-fieldiq-text mt-0.5">
                  {uncovered.map(c => `${LINE_ITEM_TYPE_LABELS[c.type]} (${c.lineItemCount})`).join(', ')}
                  {' — '}
                  no pay is suggested for line items of these types, by the AI or otherwise,
                  until a rate is set below.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-[1fr_55px_55px_55px_55px_65px_65px] gap-1.5 mb-2">
            <span className="text-xs text-fieldiq-muted font-semibold">Line Item</span>
            <span className="text-xs text-fieldiq-muted font-semibold text-center">Single%</span>
            <span className="text-xs text-fieldiq-muted font-semibold text-center">Dual%</span>
            <span className="text-xs text-fieldiq-muted font-semibold text-center">Triple%</span>
            <span className="text-xs text-fieldiq-muted font-semibold text-center">Quad%</span>
            <span className="text-xs text-fieldiq-muted font-semibold text-center">Day $</span>
            <span className="text-xs text-fieldiq-muted font-semibold text-center">Hr $</span>
          </div>

          {hiddenWithRate.length > 0 && (
            <p className="text-xs text-amber-400 mb-2 flex items-start gap-1.5" role="note">
              <EyeOff size={12} className="shrink-0 mt-0.5" />
              <span>
                {hiddenWithRate.map(t => LINE_ITEM_LABELS[t]).join(', ')}{' '}
                {hiddenWithRate.length === 1 ? 'is' : 'are'} hidden in Settings but still {hiddenWithRate.length === 1 ? 'carries' : 'carry'} a rate for this worker, so {hiddenWithRate.length === 1 ? 'it stays' : 'they stay'} listed here.
              </span>
            </p>
          )}

          {/* EVERY TYPE HIDDEN AND NO RATES ON THIS WORKER LEAVES NOTHING TO
              LIST, and the column headings above would then stand over a blank
              space that reads as a broken modal rather than as a configuration
              the tenant chose. It is a real empty state — a fact about the
              data — and it names the setting that produced it, because the
              remedy is in Settings and not here. */}
          {gridTypes.length === 0 && (
            <p data-testid="pay-rate-grid-empty" className="text-sm text-fieldiq-muted py-4">
              No line item types yet. Every type is hidden in Settings &rarr; Line Item Types,
              so there is nothing to set a rate on.
            </p>
          )}

          <div className="space-y-1.5">
            {gridTypes.map(type => {
              const row = form[type];
              const needsRate = uncovered.some(c => c.type === type);
              const isHidden = hiddenTypes.has(type);
              return (
                <div key={type} data-testid={`pay-rate-row-${type}`} className="grid grid-cols-[1fr_55px_55px_55px_55px_65px_65px] gap-1.5 items-center">
                  <span className={`text-sm truncate flex items-center gap-1.5 ${needsRate ? 'text-red-400 font-semibold' : 'text-white'}`}>
                    {needsRate && <AlertTriangle size={12} className="shrink-0" />}
                    {LINE_ITEM_LABELS[type]}
                    {isHidden && (
                      <span
                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-400 border border-amber-400/40 rounded px-1 py-px"
                        title="Hidden in Settings → Line Item Types, but this worker has a rate on it"
                      >
                        <EyeOff size={10} />
                        Hidden
                      </span>
                    )}
                  </span>
                  <input
                    type="number"
                    step="0.5"
                    value={row.single}
                    onChange={e => updateField(type, 'single', e.target.value)}
                    placeholder="—"
                    className="w-full text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-1.5 py-1.5 text-white text-center outline-none focus:border-fieldiq-accent"
                  />
                  <input
                    type="number"
                    step="0.5"
                    value={row.dual}
                    onChange={e => updateField(type, 'dual', e.target.value)}
                    placeholder="—"
                    className="w-full text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-1.5 py-1.5 text-white text-center outline-none focus:border-fieldiq-accent"
                  />
                  <input
                    type="number"
                    step="0.5"
                    value={row.triple}
                    onChange={e => updateField(type, 'triple', e.target.value)}
                    placeholder="—"
                    className="w-full text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-1.5 py-1.5 text-white text-center outline-none focus:border-fieldiq-accent"
                  />
                  <input
                    type="number"
                    step="0.5"
                    value={row.quad}
                    onChange={e => updateField(type, 'quad', e.target.value)}
                    placeholder="—"
                    className="w-full text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-1.5 py-1.5 text-white text-center outline-none focus:border-fieldiq-accent"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={row.dayRate}
                    onChange={e => updateField(type, 'dayRate', e.target.value)}
                    placeholder="—"
                    className="w-full text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-1.5 py-1.5 text-white text-center outline-none focus:border-fieldiq-accent"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={row.hourly}
                    onChange={e => updateField(type, 'hourly', e.target.value)}
                    placeholder="—"
                    className="w-full text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-1.5 py-1.5 text-white text-center outline-none focus:border-fieldiq-accent"
                  />
                  {type === 'shop' && (
                    // Per WORKER, not per tenant: a field hand doing shop time
                    // in a slow period is typically agreed to no OT on those
                    // hours; a dedicated shop hand or mechanic gets it. Off by
                    // default (API 0384); a future overtime rule cannot switch
                    // it on for anyone the office has not.
                    <label
                      data-testid="pay-rate-shop-overtime-eligible"
                      className="col-span-7 flex items-center gap-2 pl-4 text-xs text-fieldiq-muted cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        checked={row.overtimeEligible === true}
                        onChange={e => updateField(type, 'overtimeEligible', e.target.checked)}
                        className="accent-fieldiq-accent"
                      />
                      Shop hours count toward overtime for this worker
                      <span className="text-[10px] text-fieldiq-muted/70">(needs an Hr $ on this row; off = shop hours never earn OT)</span>
                    </label>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-fieldiq-muted mt-3">
            Enter percentages as whole numbers (e.g. 25 for 25%). Leave blank for N/A.
          </p>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-fieldiq-border shrink-0">
          <button
            onClick={onClose}
            className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save Pay Rates'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Pay Profile Tab Component ─── */

const WORKER_TYPE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'owner_operator', label: 'Owner Operator' },
] as const;


const CURRENCY_OPTIONS = [
  { value: 'CAD', label: 'CAD' },
  { value: 'USD', label: 'USD' },
] as const;

const WORKER_TYPE_LABELS: Record<string, string> = { employee: 'Employee', contractor: 'Contractor', owner_operator: 'Owner Operator' };

// `currency` was accepted and never read, so a US$ profile printed a bare '$'.
// An absent rate is an em dash with no unit, not "$0.00 / hr".
function formatBaseRate(payBasis: string, baseRate: number | string | null | undefined, currency: string) {
  // The stored base rate on a percentage profile is the API's required 0, not a
  // rate: the worker is paid from tickets. "$0.00 / hr" would read as a decision.
  if (payBasis === 'percentage') return 'Paid from tickets';
  if (payBasis === 'bol_percent') return `${Number(baseRate || 0)}% of BOL`;
  const unit = payBasis === 'salary' ? ' / year' : payBasis === 'day_rate' ? ' / day' : ' / hr';
  return fmtCurrency(baseRate, currency, 2, unit);
}

function baseRateLabel(payBasis: string) {
  if (payBasis === 'bol_percent') return 'Percentage of BOL (%)';
  if (payBasis === 'salary') return 'Annual Salary ($/yr)';
  if (payBasis === 'day_rate') return 'Day Rate ($/day)';
  return 'Hourly Rate ($/hr)';
}

function PayProfileTab({ userId, onToast }: { userId: string; onToast: (msg: string) => void }) {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);

  /**
   * The `.catch(() => null)` here used to swallow EVERY failure into `null`, which
   * this tab renders as "No pay profile configured" plus an Add button. For a 404
   * that is correct and deliberate — no profile yet is a real state. For the 403
   * that `/pay-profiles` gained in API #358 it is a false statement about the
   * worker, followed by an invitation to create a record that already exists.
   *
   * So: swallow the absent case, re-throw the refusal, and let the render branch
   * on it. This tab's values were already honest about absence — `fmtRate` returns
   * `—` rather than `$0` — but "absent" was the wrong fact to be honest about.
   */
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['pay-profile', userId],
    queryFn: () =>
      api.payProfiles.getByUser(userId)
        .then((r: any) => r.data?.data ?? r.data ?? null)
        .catch((err: any) => {
          if (isForbidden(err)) throw err;
          return null;
        }),
    enabled: !!userId,
    retry: false,
  });

  const forbidden = isForbidden(error);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  // `effective_date` on a pay-rate row is a CALENDAR DAY — the day the rate
  // starts applying, not an instant. Its only call site is fmtDate(profile.effective_date).
  const fmtDate = (d: string | null) => {
    return formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-CA') ?? '—';
  };

  // Same optional-rate convention as the pay-rate grid above: 0 means unset.
  // The absent case used to render '$—' — a dollar sign in front of "we do not
  // know", which is the exact shape fmtCurrency's doc warns call sites against.
  const fmtRate = (v: any) =>
    v == null || v === '' || Number(v) === 0 ? '—' : fmtCurrency(v);

  if (forbidden) {
    return (
      <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border">
        <PermissionNotice detail="Pay rates are visible to admins and office staff. The rest of this employee's record is unaffected." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!profile ? (
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-8 text-center space-y-3">
          <DollarSign className="w-10 h-10 text-fieldiq-muted mx-auto mb-1" />
          <p className="text-fieldiq-text text-sm">No pay profile configured</p>
          <button
            onClick={() => setShowDialog(true)}
            className="inline-flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-amber-400 transition-colors"
          >
            <Plus size={14} />
            Add Pay Profile
          </button>
        </div>
      ) : (
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-fieldiq-border bg-fieldiq-dark/30">
            <span className="text-xs font-semibold text-fieldiq-accent uppercase tracking-wide">Pay Configuration</span>
            <button
              onClick={() => setShowDialog(true)}
              className="flex items-center gap-1 text-xs text-fieldiq-muted hover:text-fieldiq-accent transition-colors"
            >
              <Edit3 size={12} />
              Edit
            </button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-fieldiq-muted">Pay Type</span>
              <span className="text-sm text-white font-medium">{payBasisLabel(profile.pay_basis)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-fieldiq-muted">Base Rate</span>
              <span className="text-sm text-white font-medium">{formatBaseRate(profile.pay_basis, profile.base_rate, profile.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-fieldiq-muted">Currency</span>
              <span className="text-sm text-white font-medium">{profile.currency || 'CAD'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-fieldiq-muted">Worker Type</span>
              <span className="text-sm text-white font-medium">{WORKER_TYPE_LABELS[profile.worker_type] || profile.worker_type}</span>
            </div>

            {/* Add-on rates */}
            <div className="pt-2 border-t border-fieldiq-border">
              <p className="text-xs text-fieldiq-muted mb-2">Add-on Rates</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex justify-between">
                  <span className="text-xs text-fieldiq-muted">Standby</span>
                  <span className="text-xs text-white">{fmtRate(profile.standby_rate)} /hr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-fieldiq-muted">Travel</span>
                  <span className="text-xs text-white">{fmtRate(profile.travel_rate)} /hr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-fieldiq-muted">OT Override</span>
                  <span className="text-xs text-white">{fmtRate(profile.ot_rate_override)} /hr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-fieldiq-muted">H2S Prem</span>
                  <span className="text-xs text-white">{fmtRate(profile.h2s_premium_rate)} /hr</span>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-fieldiq-border flex justify-between">
              <span className="text-sm text-fieldiq-muted">Effective</span>
              <span className="text-sm text-white font-medium">{fmtDate(profile.effective_date)}</span>
            </div>
          </div>
        </div>
      )}

      {showDialog && (
        <EditPayProfileDialog
          userId={userId}
          existing={profile}
          onClose={() => setShowDialog(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['pay-profile', userId] });
            queryClient.invalidateQueries({ queryKey: ['user', userId] });
            setShowDialog(false);
            onToast('Pay profile saved');
          }}
        />
      )}
    </div>
  );
}

export function EditPayProfileDialog({ userId, existing, onClose, onSuccess }: {
  userId: string;
  existing: any;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isCreate = !existing;
  const timeZone = useTenantTimeZone();
  const [workerType, setWorkerType] = useState(existing?.worker_type || 'employee');
  /**
   * An EXISTING profile keeps the basis it has — this dialog never re-bases a
   * worker. A NEW one starts at the tenant's default from the API
   * (useDefaultPayBasis: US → Hourly, Canada with earthworks → Hourly, else
   * Percentage). It used to start at Hourly everywhere, which is harmless only
   * while a Canadian Hourly profile pays nothing differently; see lib/pay-basis.ts.
   */
  const tenantDefaultBasis = useDefaultPayBasis(isCreate);
  const [chosenBasis, setPayBasis] = useState<string | null>(existing?.pay_basis ?? null);
  const payBasis: string | null = chosenBasis ?? (isCreate ? tenantDefaultBasis : null);
  const basisOptions = dialogBasisOptions(existing?.pay_basis)
    .map(value => ({ value, label: PAY_BASIS_LABELS[value] }));
  const [currency, setCurrency] = useState(existing?.currency || 'CAD');
  const [baseRate, setBaseRate] = useState(existing?.base_rate?.toString() || '');
  const [standbyRate, setStandbyRate] = useState(existing?.standby_rate?.toString() || '');
  const [travelRate, setTravelRate] = useState(existing?.travel_rate?.toString() || '');
  const [otRateOverride, setOtRateOverride] = useState(existing?.ot_rate_override?.toString() || '');
  const [h2sPremiumRate, setH2sPremiumRate] = useState(existing?.h2s_premium_rate?.toString() || '');
  const [effectiveDate, setEffectiveDate] = useState(existing?.effective_date || todayCalendarDate(timeZone) || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isPercentage = payBasis === 'percentage';

  const handleSave = async () => {
    // A new profile with no basis is refused, not sent: the API would store its
    // own default beside a rate typed under a $/hr label (see WorkerPayProfileScreen).
    if (isCreate && !payBasis) {
      setError('Choose a pay basis');
      return;
    }
    if (!isPercentage && (!baseRate || isNaN(Number(baseRate)))) {
      setError('Base rate is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        worker_type: workerType,
        // Omitted rather than guessed when nothing decided it (see `payBasis`).
        ...(payBasis ? { pay_basis: payBasis } : {}),
        base_rate: isPercentage ? PERCENTAGE_BASE_RATE : Number(baseRate),
        currency,
        standby_rate: standbyRate ? Number(standbyRate) : null,
        travel_rate: travelRate ? Number(travelRate) : null,
        ot_rate_override: otRateOverride ? Number(otRateOverride) : null,
        h2s_premium_rate: h2sPremiumRate ? Number(h2sPremiumRate) : null,
        effective_date: effectiveDate,
      };
      if (isCreate) {
        await api.payProfiles.createByUser(userId, payload);
      } else {
        await api.payProfiles.updateByUser(userId, payload);
      }
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to save pay profile';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-fieldiq-accent transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border">
          <h2 className="text-base font-bold text-white">{isCreate ? 'Add Pay Profile' : 'Edit Pay Profile'}</h2>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-3 py-2 rounded-lg">{error}</div>
          )}

          {/* Worker Type */}
          <div>
            <label className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide mb-2 block">Worker Type</label>
            <div className="flex gap-2">
              {WORKER_TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setWorkerType(opt.value)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    workerType === opt.value
                      ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-fieldiq-muted border-fieldiq-border hover:border-fieldiq-accent/50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Pay Type */}
          <div>
            <label className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide mb-2 block">Pay Type</label>
            <div className="grid grid-cols-2 gap-2">
              {basisOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPayBasis(opt.value)}
                  className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    payBasis === opt.value
                      ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-fieldiq-muted border-fieldiq-border hover:border-fieldiq-accent/50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Currency */}
          <div>
            <label className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide mb-2 block">Currency</label>
            <div className="flex gap-2">
              {CURRENCY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setCurrency(opt.value)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    currency === opt.value
                      ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-fieldiq-muted border-fieldiq-border hover:border-fieldiq-accent/50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Base Rate — none for a percentage worker, who is paid from tickets. */}
          {isPercentage ? (
            <p data-testid="pay-profile-percentage-note" className="text-sm text-fieldiq-muted">
              Paid from tickets, at the percentages on this worker&rsquo;s pay rates. No base rate is used.
            </p>
          ) : (
          <div>
            <label className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide mb-2 block">{baseRateLabel(payBasis ?? '')}</label>
            <input
              type="number"
              step={payBasis === 'bol_percent' ? '0.1' : '0.01'}
              value={baseRate}
              onChange={e => setBaseRate(e.target.value)}
              placeholder={payBasis === 'bol_percent' ? 'e.g. 28' : 'e.g. 28.50'}
              className={inputCls}
            />
          </div>
          )}

          {/* Add-on Rates */}
          <div>
            <p className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide mb-2">Add-on Rates (optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Standby ($/hr)</label>
                <input type="number" step="0.01" value={standbyRate} onChange={e => setStandbyRate(e.target.value)} placeholder="—" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Travel ($/hr)</label>
                <input type="number" step="0.01" value={travelRate} onChange={e => setTravelRate(e.target.value)} placeholder="—" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">OT Override ($/hr)</label>
                <input type="number" step="0.01" value={otRateOverride} onChange={e => setOtRateOverride(e.target.value)} placeholder="—" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">H2S Premium ($/hr)</label>
                <input type="number" step="0.01" value={h2sPremiumRate} onChange={e => setH2sPremiumRate(e.target.value)} placeholder="—" className={inputCls} />
              </div>
            </div>
          </div>

          {/* Effective Date */}
          <div>
            <label className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide mb-2 block">Effective Date</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={e => setEffectiveDate(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-fieldiq-border">
          <button
            onClick={onClose}
            className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save Pay Profile'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Timesheets Tab ─── */
function TimesheetsTab({ userId }: { userId: string }) {
  const { data: timesheets = [], isLoading } = useQuery({
    queryKey: ['employee-timesheets', userId],
    queryFn: () =>
      apiClient.get(`/employees/${userId}/timesheets`).then((r: any) => r.data?.data ?? r.data ?? []),
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  if (timesheets.length === 0) {
    return (
      <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-8 text-center">
        <Clock className="w-10 h-10 text-fieldiq-muted mx-auto mb-3" />
        <p className="text-fieldiq-text text-sm">No timesheets yet</p>
      </div>
    );
  }

  const statusPill = (status: string) => {
    const styles: Record<string, string> = {
      open: 'bg-amber-500/20 text-amber-400',
      approved: 'bg-green-500/20 text-green-400',
      exported: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
    };
    const labels: Record<string, string> = {
      open: 'Pending',
      approved: 'Approved',
      exported: 'Paid',
    };
    return (
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {labels[status] || status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Both operands are CALENDAR DAYS (`period_start` / `period_end`), so the
  // whole helper converts — there is no instant mixed in here to split out.
  const fmtPeriod = (start: string, end: string) => {
    const sStr = formatCalendarDate(start, { month: 'short', day: 'numeric' }, 'en-CA');
    const eStr = formatCalendarDate(end, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-CA');
    if (!sStr || !eStr) return '—';
    return `${sStr} – ${eStr}`;
  };

  // fmtCurrency is imported from src/lib/display-labels. It used to be redefined
  // here as `Number(amount || 0)`, so a timesheet the server could not total
  // rendered as "CA$0.00" — a hand who earned nothing, rather than a number the
  // server does not have. The CA$/US$ prefix this copy existed for now lives in
  // the shared helper as its second argument.

  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
      <div className="grid grid-cols-4 px-5 py-3 border-b border-fieldiq-border bg-fieldiq-dark/30">
        <span className="text-xs font-semibold text-fieldiq-accent">Period</span>
        <span className="text-xs font-semibold text-fieldiq-accent text-center">Type</span>
        <span className="text-xs font-semibold text-fieldiq-accent text-right">Total</span>
        <span className="text-xs font-semibold text-fieldiq-accent text-center">Status</span>
      </div>
      {timesheets.map((ts: any, i: number) => (
        <div key={ts.id || i} className={`grid grid-cols-4 px-5 py-3.5 items-center ${i < timesheets.length - 1 ? 'border-b border-fieldiq-border' : ''}`}>
          <span className="text-sm text-white">{fmtPeriod(ts.period_start, ts.period_end)}</span>
          <span className="text-sm text-white text-center capitalize">{ts.period_type || '—'}</span>
          <span className="text-sm text-green-400 text-right">{fmtCurrency(ts.total_earnings, ts.currency)}</span>
          <span className="text-center">{statusPill(ts.period_status || ts.status || 'open')}</span>
        </div>
      ))}
    </div>
  );
}


/**
 * How this person's approved PERSONAL expenses are paid back (api#1024, FE-5):
 * Default / Payroll / Reimbursement report.
 *
 * ALWAYS SHOWS WHAT DEFAULT RESOLVES TO — "Default (report — no current pay
 * rate)" — from the API's `expenseReimbursementResolved`, so a NULL setting is
 * never a mystery and the rule is never re-derived here.
 *
 * DEFAULT SENDS `null`, NOT 'report'. The three options are three different
 * stored values: null follows the rule (and moves if the person gains a pay
 * rate), 'report' pins them to the report. Collapsing Default into whichever
 * value it happens to resolve to today would silently freeze the rule.
 *
 * The write is PUT /payroll/workers/:id/expense-reimbursement — admin STRICT,
 * the same door and gate as job-pay-exemption — so only an admin sees the
 * control. Everyone else who can open the profile sees the sentence.
 */
type ReimbursementChoice = 'default' | 'payroll' | 'report';

export function reimbursementChoiceToBody(choice: ReimbursementChoice): 'payroll' | 'report' | null {
  return choice === 'default' ? null : choice;
}

function ExpenseReimbursementSection({ employeeId, stored, resolved, onToast }: {
  employeeId: string;
  stored: unknown;
  resolved: { path?: string; source?: string; reason?: string } | null;
  onToast: (msg: string) => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const mayEdit = canSetJobPayExemption(user?.role); // admin strict, as the API's door
  const current: ReimbursementChoice = stored === 'payroll' || stored === 'report' ? stored : 'default';
  const [choice, setChoice] = useState<ReimbursementChoice>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedWord = resolved?.path === 'payroll' ? 'payroll' : resolved?.path === 'report' ? 'reimbursement report' : null;
  const defaultLabel = `Default${resolved?.source === 'default' && resolvedWord ? ` (${resolvedWord})` : ''}`;
  const sentence = current === 'default'
    ? (resolved?.reason ?? 'Default rule')
    : current === 'payroll' ? 'Set by the office: payroll' : 'Set by the office: reimbursement report';

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.payroll.setExpenseReimbursement(employeeId, reimbursementChoiceToBody(choice));
      queryClient.invalidateQueries({ queryKey: ['user', employeeId] });
      onToast('Expense reimbursement updated');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save the expense reimbursement setting.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5" data-testid="expense-reimbursement-section">
      <p className="text-xs text-fieldiq-muted uppercase tracking-wide">Expense reimbursement</p>
      <p className="text-sm font-bold text-white mt-1" data-testid="expense-reimbursement-current">{sentence}</p>
      <p className="text-xs text-fieldiq-muted mt-1">
        Where their approved personal receipts are paid back: on a pay period, or on the monthly reimbursement report.
      </p>
      {mayEdit && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Expense reimbursement">
            {(['default', 'payroll', 'report'] as const).map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={choice === c}
                onClick={() => setChoice(c)}
                className={`rounded-lg px-3 py-2 text-xs font-semibold border ${
                  choice === c ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent' : 'bg-fieldiq-dark text-white border-fieldiq-border'
                }`}
              >
                {c === 'default' ? defaultLabel : c === 'payroll' ? 'Payroll' : 'Reimbursement report'}
              </button>
            ))}
          </div>
          {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
          <button
            type="button"
            onClick={save}
            disabled={saving || choice === current}
            className="bg-fieldiq-accent text-fieldiq-dark px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save reimbursement setting'}
          </button>
        </div>
      )}
    </div>
  );
}
